import { useMemo } from "react";
import type { TaskListQuery } from "@common/contracts/task.contract";
import type { TaskGroupBy } from "@common/types/task";
import type { View } from "@common/types/views/views";
import type { TaskWorkspaceData } from "../useTaskWorkspaceData";
import type { TaskBoardTask } from "../types";
import type { TaskBoardDisplayState } from "./taskBoardCache";
import type {
    TaskBoardCollapseByView,
    UpdateTaskBoardCollapse,
} from "./taskBoardCollapse";
import type { TaskBoardLaneBy } from "./taskBoardLanes";
import { getTaskBoardSort } from "./taskBoardSort";
import { getTaskBoardCardFields } from "./taskCardColumns";
import type { TaskViewBoardDisplay } from "./taskBoardViewDisplay";
import { useTaskBoardCollapse } from "./useTaskBoardCollapse";
import { useTaskBoardModel } from "./useTaskBoardModel";

const NO_TASKS: TaskBoardTask[] = [];

interface UseTaskBoardDisplayArgs {
    /** The page's tasks on Board; null while they load, and on List. */
    tasks: TaskBoardTask[] | null;
    /** List's sort and status filter, which Board shares. */
    filters: Pick<TaskListQuery, "sort" | "status">;
    viewDisplay: TaskViewBoardDisplay;
    /** The view's visible columns, which are the card fields. */
    viewColumns: View["columns"];
    groupBy: TaskGroupBy;
    laneBy: TaskBoardLaneBy | null;
    workspaceData: TaskWorkspaceData;
    isCrossWorkspace: boolean;
    /** The view picked in the views menu, which keeps its own collapse state; null with none. */
    viewId: string | null;
    collapseByViewId: TaskBoardCollapseByView;
    onCollapseByViewIdChange: UpdateTaskBoardCollapse;
}

/**
 * What the board shows: its model in List's sort, the view's collapsed
 * columns and lanes, and the display state that combines them.
 */
export const useTaskBoardDisplay = ({
    tasks,
    filters,
    viewDisplay,
    viewColumns,
    groupBy,
    laneBy,
    workspaceData,
    isCrossWorkspace,
    viewId,
    collapseByViewId,
    onCollapseByViewIdChange,
}: UseTaskBoardDisplayArgs) => {
    const sortedDisplay = useMemo(
        () => ({ ...viewDisplay, ...getTaskBoardSort(filters.sort) }),
        [viewDisplay, filters.sort],
    );
    const model = useTaskBoardModel({
        tasks: tasks ?? NO_TASKS,
        groupBy,
        statusFilter: filters.status,
        workspaceData,
        display: sortedDisplay,
        laneBy,
        isCrossWorkspace,
    });
    const collapse = useTaskBoardCollapse({
        viewId,
        collapseByViewId,
        onCollapseByViewIdChange,
        board: tasks === null ? null : model,
    });
    const { collapsedColumnIds, collapsedLaneIds } = collapse;
    const display = useMemo<TaskBoardDisplayState>(
        () => ({
            ...sortedDisplay,
            cardFields: getTaskBoardCardFields({
                columns: viewColumns,
                isCommentsShown: sortedDisplay.isCommentsShown,
            }),
            collapsedColumnIds,
            collapsedLaneIds,
        }),
        [sortedDisplay, viewColumns, collapsedColumnIds, collapsedLaneIds],
    );
    return { model, collapse, display };
};
